import chroma from "chroma-js";

// Clean, simple color theme enum based on industry standards
export enum ColorTheme {
  // Categorical themes (for different data categories)
  OBSERVABLE_10 = "observable10", // Observable Plot default (10 colors)
  OBSERVABLE_20 = "observable20", // Observable Plot extended (20 colors)
  COLORBREWER_SET1 = "set1", // ColorBrewer Set1 (distinct colors)
  COLORBREWER_SET2 = "set2", // ColorBrewer Set2 (softer colors)
  COLORBREWER_PAIRED = "paired", // ColorBrewer Paired (paired categories)
  COLORBREWER_ACCENT = "accent", // ColorBrewer Accent (bright colors)
  ACCESSIBLE = "accessible", // Colorblind-friendly palette

  // Professional themes
  CORPORATE = "corporate", // Ultra-muted for C-suite presentations
  MODERN = "modern", // Clean contemporary design system colors
  ACCESSIBLE_PRO = "accessible_pro", // Enhanced colorblind-friendly with better distinction

  // Sequential themes (for ordered data)
  VIRIDIS = "viridis", // Viridis (purple to yellow)
  PLASMA = "plasma", // Plasma (purple to pink)
  BLUES = "blues", // Blue monochromatic
  GREENS = "greens", // Green monochromatic

  // Diverging themes (for data with meaningful center)
  RED_BLUE = "red_blue", // Red to Blue diverging
  BROWN_GREEN = "brown_green", // Brown to Green diverging
}

// Industry-standard categorical palettes
const CATEGORICAL_PALETTES: Record<string, string[]> = {
  // Observable Plot Category 10 (professional muted version)
  [ColorTheme.OBSERVABLE_10]: [
    "#4b71bd", // desaturated slate blue
    "#cb463f", // desaturated bold red
    "#e58f1a", // desaturated orange
    "#2a8d35", // desaturated green
    "#8f3b8f", // desaturated purple
    "#2d9cae", // desaturated cyan
    "#c35b76", // desaturated rose
    "#5f9d1c", // desaturated lime
    "#a94040", // desaturated dark red
    "#4468a1", // desaturated navy
  ],

  // Observable Plot Category 20 (professional muted version)
  [ColorTheme.OBSERVABLE_20]: [
    "#4b71bd",
    "#a7ccef", // blues
    "#cb463f",
    "#ffaaaa", // reds
    "#e58f1a",
    "#f7cd8c", // oranges
    "#2a8d35",
    "#83cd83", // greens
    "#8f3b8f",
    "#d1aad1", // purples
    "#2d9cae",
    "#8dd9dd", // teals
    "#c35b76",
    "#ffb3d1", // pinks
    "#5f9d1c",
    "#dbf97b", // lime
    "#a94040",
    "#ff7a7a", // dark red
    "#4468a1",
    "#a3c3f0", // navy blue
  ],

  // ColorBrewer Set1 (maximally distinct colors)
  [ColorTheme.COLORBREWER_SET1]: [
    "#e41a1c", // red
    "#377eb8", // blue
    "#4daf4a", // green
    "#984ea3", // purple
    "#ff7f00", // orange
    "#ffff33", // yellow
    "#a65628", // brown
    "#f781bf", // pink
    "#999999", // gray
  ],

  // ColorBrewer Set2 (softer, more muted colors)
  [ColorTheme.COLORBREWER_SET2]: [
    "#66c2a5", // teal
    "#fc8d62", // orange
    "#8da0cb", // blue
    "#e78ac3", // pink
    "#a6d854", // green
    "#ffd92f", // yellow
    "#e5c494", // tan
    "#b3b3b3", // gray
  ],

  // ColorBrewer Paired (colors in complementary pairs)
  [ColorTheme.COLORBREWER_PAIRED]: [
    "#a6cee3",
    "#1f78b4", // light blue, blue
    "#b2df8a",
    "#33a02c", // light green, green
    "#fb9a99",
    "#e31a1c", // light red, red
    "#fdbf6f",
    "#ff7f00", // light orange, orange
    "#cab2d6",
    "#6a3d9a", // light purple, purple
    "#ffff99",
    "#b15928", // light yellow, brown
  ],

  // ColorBrewer Accent (bright, vivid colors)
  [ColorTheme.COLORBREWER_ACCENT]: [
    "#7fc97f", // green
    "#beaed4", // purple
    "#fdc086", // orange
    "#ffff99", // yellow
    "#386cb0", // blue
    "#f0027f", // magenta
    "#bf5b17", // brown
    "#666666", // gray
  ],

  // Accessible palette (colorblind-friendly)
  [ColorTheme.ACCESSIBLE]: [
    "#1f77b4", // blue (safe)
    "#ff7f0e", // orange (safe)
    "#2ca02c", // green (safe)
    "#d62728", // red (distinguishable)
    "#17becf", // cyan (safe)
    "#8c564b", // brown (safe)
    "#e377c2", // pink (distinguishable)
    "#7f7f7f", // gray (neutral)
    "#bcbd22", // olive (safe)
  ],

  // Corporate - Ultra-muted for professional presentations
  [ColorTheme.CORPORATE]: [
    "#4D5B6B", // navy gray
    "#7C6F64", // taupe
    "#4E6E58", // sage green
    "#84596B", // muted plum
    "#4E707A", // slate
    "#766C8A", // lavender gray
    "#8A6F64", // earthy clay
    "#675A8A", // deep indigo
    "#597A8A", // marine blue
    "#788A64", // moss green
  ],

  // Modern - Clean contemporary design system colors
  [ColorTheme.MODERN]: [
    "#4a85e6", // softened blue
    "#1fa97b", // softened green
    "#e1991e", // muted amber
    "#df5353", // softened red
    "#9068e9", // softened purple
    "#16aac3", // toned-down cyan
    "#e77627", // warm orange
    "#80bb26", // olive lime
    "#dc5799", // softened pink
    "#757575", // neutral gray
  ],

  // Accessible Pro - Enhanced colorblind-friendly with better distinction
  [ColorTheme.ACCESSIBLE_PRO]: [
    "#0073E6", // strong blue
    "#E68A00", // strong orange
    "#009E73", // strong green
    "#CC79A7", // strong pink
    "#56B4E9", // light blue
    "#F0E442", // strong yellow
    "#D55E00", // strong red-orange
    "#999999", // medium gray
    "#000000", // black
  ],
};

// Sequential and diverging palettes using chroma.js built-ins
const SEQUENTIAL_DIVERGING_GENERATORS: Record<
  string,
  (count: number) => string[]
> = {
  [ColorTheme.VIRIDIS]: (count) =>
    chroma
      .scale([
        "#440154",
        "#482777",
        "#3f4a8a",
        "#31678e",
        "#26838f",
        "#1f9d8a",
        "#6cce5a",
        "#b6de2b",
        "#fee825",
      ])
      .colors(count),
  [ColorTheme.PLASMA]: (count) =>
    chroma
      .scale(["#0d0887", "#6a00a8", "#b12a90", "#e16462", "#fca636", "#f0f921"])
      .colors(count),
  [ColorTheme.BLUES]: (count) =>
    chroma.scale(["#f7fbff", "#08519c"]).colors(count),
  [ColorTheme.GREENS]: (count) =>
    chroma.scale(["#f7fcf5", "#00441b"]).colors(count),
  [ColorTheme.RED_BLUE]: (count) =>
    chroma.scale(["#d7191c", "#ffffff", "#2c7bb6"]).colors(count),
  [ColorTheme.BROWN_GREEN]: (count) =>
    chroma.scale(["#8c510a", "#f5f5f5", "#01665e"]).colors(count),
};

// Simple hash function for consistent color assignment
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.codePointAt(i)!;
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Main function: Get consistent color for a group name
export function getConsistentColor(
  groupName: string,
  theme: ColorTheme = ColorTheme.OBSERVABLE_10,
  mode: "light" | "dark" = "light",
  totalCategories?: number
): string {
  const palette = getThemePalette(theme, Math.max(10, totalCategories || 10));
  const hash = simpleHash(groupName);
  let color = palette[hash % palette.length];

  // Smart saturation adjustment based on number of categories
  if (totalCategories && totalCategories > 8) {
    // Reduce saturation when there are many categories to avoid visual chaos
    const saturationReduction = Math.min(0.4, (totalCategories - 8) * 0.05);
    color = chroma(color).desaturate(saturationReduction).hex();
  }

  // Adjust for dark mode if needed
  if (mode === "dark") {
    return chroma(color).brighten(0.5).hex();
  }

  return color;
}

// Get full color palette for multiple group names
export function getConsistentColorPalette(
  groupNames: string[],
  theme: ColorTheme = ColorTheme.OBSERVABLE_10,
  mode: "light" | "dark" = "light"
): Record<string, string> {
  if (groupNames.length === 0) return {};

  const totalCategories = groupNames.length;
  const palette = getThemePalette(theme, Math.max(10, totalCategories));
  const result: Record<string, string> = {};

  // Sort group names for consistent assignment
  const sortedNames = [...groupNames].sort();

  // Smart saturation adjustment for many categories
  const saturationReduction =
    totalCategories > 8 ? Math.min(0.4, (totalCategories - 8) * 0.05) : 0;

  sortedNames.forEach((name) => {
    const hash = simpleHash(name);
    let color = palette[hash % palette.length];

    // Apply saturation reduction if needed
    if (saturationReduction > 0) {
      color = chroma(color).desaturate(saturationReduction).hex();
    }

    // Adjust for dark mode if needed
    result[name] = mode === "dark" ? chroma(color).brighten(0.5).hex() : color;
  });

  return result;
}

// Get theme palette (categorical or generated)
function getThemePalette(theme: ColorTheme, count: number): string[] {
  // For categorical themes, use predefined palettes
  if (CATEGORICAL_PALETTES[theme]) {
    const palette = CATEGORICAL_PALETTES[theme];

    // If we need more colors than available, cycle through the palette
    if (count <= palette.length) {
      return palette.slice(0, count);
    }

    // For larger counts, repeat the palette
    const result: string[] = [];
    for (let i = 0; i < count; i++) {
      result.push(palette[i % palette.length]);
    }
    return result;
  }

  // For sequential/diverging themes, generate the requested count
  if (SEQUENTIAL_DIVERGING_GENERATORS[theme]) {
    return SEQUENTIAL_DIVERGING_GENERATORS[theme](count);
  }

  // Fallback to Observable 10
  return CATEGORICAL_PALETTES[ColorTheme.OBSERVABLE_10];
}

// Get theme metadata for UI display
export function getThemeInfo(theme: ColorTheme): {
  name: string;
  description: string;
  type: "categorical" | "sequential" | "diverging";
} {
  const themeInfo: Record<
    ColorTheme,
    {
      name: string;
      description: string;
      type: "categorical" | "sequential" | "diverging";
    }
  > = {
    [ColorTheme.OBSERVABLE_10]: {
      name: "Professional",
      description:
        "Standard 10-color categorical palette for data visualization",
      type: "categorical",
    },
    [ColorTheme.OBSERVABLE_20]: {
      name: "Data Rich",
      description:
        "Extended 20-color categorical palette with paired variations",
      type: "categorical",
    },
    [ColorTheme.COLORBREWER_SET1]: {
      name: "Distinct",
      description: "Maximally distinct colors for clear category separation",
      type: "categorical",
    },
    [ColorTheme.COLORBREWER_SET2]: {
      name: "Soft Distinct",
      description: "Softer, more muted categorical colors",
      type: "categorical",
    },
    [ColorTheme.COLORBREWER_PAIRED]: {
      name: "Complementary",
      description: "Colors arranged in complementary pairs",
      type: "categorical",
    },
    [ColorTheme.COLORBREWER_ACCENT]: {
      name: "Vivid",
      description: "Bright, vivid colors for emphasis",
      type: "categorical",
    },
    [ColorTheme.ACCESSIBLE]: {
      name: "Accessible",
      description: "Colorblind-friendly palette with high distinguishability",
      type: "categorical",
    },
    [ColorTheme.CORPORATE]: {
      name: "Corporate",
      description:
        "Ultra-muted, sophisticated colors for executive presentations",
      type: "categorical",
    },
    [ColorTheme.MODERN]: {
      name: "Modern",
      description: "Clean, contemporary colors from modern design systems",
      type: "categorical",
    },
    [ColorTheme.ACCESSIBLE_PRO]: {
      name: "Accessible Bold",
      description:
        "Enhanced colorblind-friendly palette with maximum distinction",
      type: "categorical",
    },
    [ColorTheme.VIRIDIS]: {
      name: "Viridis",
      description: "Perceptually uniform purple to yellow progression",
      type: "sequential",
    },
    [ColorTheme.PLASMA]: {
      name: "Plasma",
      description: "Perceptually uniform purple to pink progression",
      type: "sequential",
    },
    [ColorTheme.BLUES]: {
      name: "Blues",
      description: "Light to dark blue monochromatic progression",
      type: "sequential",
    },
    [ColorTheme.GREENS]: {
      name: "Greens",
      description: "Light to dark green monochromatic progression",
      type: "sequential",
    },
    [ColorTheme.RED_BLUE]: {
      name: "Red-Blue Diverging",
      description: "Red to blue diverging scale with neutral center",
      type: "diverging",
    },
    [ColorTheme.BROWN_GREEN]: {
      name: "Brown-Green Diverging",
      description: "Brown to green diverging scale for natural data",
      type: "diverging",
    },
  };

  return themeInfo[theme] || themeInfo[ColorTheme.OBSERVABLE_10];
}

// Utility: Get preview colors for theme picker
export function getThemePreview(theme: ColorTheme): string[] {
  return getThemePalette(theme, 8);
}

// Utility: Check if color is light or dark (for text contrast)
export function isColorLight(color: string): boolean {
  return chroma(color).luminance() > 0.5;
}

// Utility: Get contrasting text color
export function getContrastTextColor(backgroundColor: string): string {
  return isColorLight(backgroundColor) ? "#000000" : "#ffffff";
}

// Legacy compatibility - simple rainbow function
export function rainbow(
  dataKey: string,
  theme: ColorTheme = ColorTheme.OBSERVABLE_10,
  mode: "light" | "dark" = "light",
  totalCategories?: number
): string {
  return getConsistentColor(dataKey, theme, mode, totalCategories);
}

// Config conversion function for backwards compatibility
export function toColorRampConfig(data: any): { theme: ColorTheme } {
  return {
    theme: data?.theme || data?.ramp || ColorTheme.OBSERVABLE_10,
  };
}

// Get color scale for heatmaps and other continuous data visualizations
export function getColorScale(theme: ColorTheme): chroma.Scale {
  // For sequential/diverging themes, return the appropriate scale
  switch (theme) {
    case ColorTheme.VIRIDIS: {
      return chroma.scale([
        "#440154",
        "#482777",
        "#3f4a8a",
        "#31678e",
        "#26838f",
        "#1f9d8a",
        "#6cce5a",
        "#b6de2b",
        "#fee825",
      ]);
    }
    case ColorTheme.PLASMA: {
      return chroma.scale([
        "#0d0887",
        "#6a00a8",
        "#b12a90",
        "#e16462",
        "#fca636",
        "#f0f921",
      ]);
    }
    case ColorTheme.BLUES: {
      return chroma.scale(["#f7fbff", "#08519c"]);
    }
    case ColorTheme.GREENS: {
      return chroma.scale(["#f7fcf5", "#00441b"]);
    }
    case ColorTheme.RED_BLUE: {
      return chroma.scale(["#d7191c", "#ffffff", "#2c7bb6"]);
    }
    case ColorTheme.BROWN_GREEN: {
      return chroma.scale(["#8c510a", "#f5f5f5", "#01665e"]);
    }
    default: {
      // For categorical themes, create a simple blue scale using the primary color
      const primaryColor = CATEGORICAL_PALETTES[theme]?.[0] || "#4E79A7";
      return chroma.scale(["#f7fbff", primaryColor]);
    }
  }
}
